import { indicationLabel } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import {
  MSK_INDICATIONS,
  MSK_REGIONS,
  type MskIndication,
  type MskRegion,
  type MskRegionData,
  type MskSide,
  type MskWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  Box,
  Button,
  Chip,
  styled,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { useState } from "react";
import { BillingCptChip } from "../billing/BillingCptChip";
import { ChipRow, chipStyle } from "../epocus-chip";
import {
  EPOCUS_AMBER,
  EPOCUS_AMBER_BG,
  EPOCUS_AMBER_BORDER,
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_GREEN,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../epocus-palette";
import type { HydratedExamImage } from "../hooks/use-epocus-exam-images";
import {
  isMskFindingShown,
  isMskRegionComplete,
  isMskRegionScanned,
  MSK_REGION_CONFIG,
  type MskFindingDef,
  type MskFindingPlacement,
  markMskRegionNormal,
  mskBillingLevel,
  mskRegionData,
  mskRegionIndications,
  mskViewKey,
  normalizeMsk,
  scannedMskRegions,
  supportsCompleteMskExam,
  untaggedMskImages,
} from "./msk-regions";
import {
  EnumChipSelect,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

export type MskRegionCode = { code: string; modifier: string | null };

const SIDE_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "bilateral", label: "Bilateral" },
];

const TAB_INDICATOR_PROPS = {
  style: { backgroundColor: EPOCUS_ICON_ACTIVE, height: 3 },
};

const RegionTabs = styled(Tabs)({
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
});

const RegionTab = styled(Tab)({
  textTransform: "none",
  fontWeight: 600,
  fontSize: rem(14),
  minHeight: 48,
  minWidth: 0,
  color: EPOCUS_TEXT_SECONDARY,
  "&.Mui-selected": {
    color: EPOCUS_ICON_ACTIVE,
  },
});

const StatusDot = styled("span")({
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: "50%",
  marginLeft: 6,
  verticalAlign: "middle",
});

const UntaggedBanner = styled(Typography)({
  backgroundColor: EPOCUS_AMBER_BG,
  border: `1px solid ${EPOCUS_AMBER_BORDER}`,
  color: EPOCUS_TEXT_PRIMARY,
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: rem(13),
});

const ThumbGrid = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
});

const ThumbCard = styled(Box)({
  width: 120,
  display: "flex",
  flexDirection: "column",
  gap: 6,
});

const ThumbFrame = styled(Box)({
  width: 120,
  height: 90,
  borderRadius: 8,
  overflow: "hidden",
  backgroundColor: EPOCUS_BG,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const ThumbImg = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

const Hint: FC<{ children: string }> = ({ children }) => (
  <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
    {children}
  </Typography>
);

const RegionTabLabel: FC<{ data: MskWorksheetData; region: MskRegion }> = ({
  data,
  region,
}) => {
  const scanned = isMskRegionScanned(data, region);
  const complete = scanned && isMskRegionComplete(data, region);
  const status = complete ? "complete" : scanned ? "incomplete" : null;

  return (
    <span>
      {MSK_REGION_CONFIG[region].label}
      {status && (
        <StatusDot
          data-testid={`msk-tab-status-${region}`}
          data-status={status}
          style={{ backgroundColor: complete ? EPOCUS_GREEN : EPOCUS_AMBER }}
        />
      )}
    </span>
  );
};

const MultiChipSelect: FC<{
  options: readonly { value: string; label: string }[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  disabled: boolean;
}> = ({ options, selected, onChange, disabled }) => (
  <ChipRow role="group">
    {options.map((option) => {
      const isSelected = selected.includes(option.value);
      return (
        <Chip
          key={option.value}
          label={option.label}
          variant="outlined"
          aria-pressed={isSelected}
          style={chipStyle(isSelected)}
          onClick={() =>
            onChange(
              isSelected
                ? selected.filter((value) => value !== option.value)
                : [...selected, option.value],
            )
          }
          disabled={disabled}
        />
      );
    })}
  </ChipRow>
);

const RegionFinding: FC<{
  finding: MskFindingDef;
  value: MskRegionData["findings"][string] | undefined;
  onChange: (value: MskRegionData["findings"][string]) => void;
  disabled: boolean;
}> = ({ finding, value, onChange, disabled }) => {
  switch (finding.kind) {
    case "single":
      return (
        <>
          <FindingLabel>{finding.label}</FindingLabel>
          <EnumChipSelect
            options={[...finding.options]}
            selected={typeof value === "string" ? value : null}
            onChange={onChange}
            disabled={disabled}
          />
        </>
      );
    case "multi":
      return (
        <>
          <FindingLabel>{finding.label}</FindingLabel>
          <MultiChipSelect
            options={finding.options}
            selected={Array.isArray(value) ? value : []}
            onChange={onChange}
            disabled={disabled}
          />
        </>
      );
    case "text":
      return (
        <>
          <FindingLabel>{finding.label}</FindingLabel>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={typeof value === "string" ? value : ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value || null)
            }
            disabled={disabled}
            inputProps={{ "aria-label": finding.label }}
          />
        </>
      );
    case "number":
      return (
        <>
          <FindingLabel>{`${finding.label} (optional)`}</FindingLabel>
          <NumericInput
            value={typeof value === "number" ? value : null}
            onChange={onChange}
            unit={finding.unit}
            ariaLabel={finding.label}
            disabled={disabled}
          />
        </>
      );
  }
};

const RegionImages: FC<{
  region: MskRegion;
  images: readonly HydratedExamImage[];
  onTagImages?: (imageIds: string[], region: MskRegion | null) => void;
  readOnly: boolean;
}> = ({ region, images, onTagImages, readOnly }) => {
  const regionLabel = MSK_REGION_CONFIG[region].label;
  const tagged = images.filter((image) => image.mskRegion === region);
  const untagged = readOnly ? [] : untaggedMskImages(images);
  const canTag = !readOnly && !!onTagImages;

  const thumb = (image: HydratedExamImage, action: React.ReactNode) => (
    <ThumbCard key={image.id} data-testid={`msk-image-${image.id}`}>
      <ThumbFrame>
        {image.previewUrl && (
          <ThumbImg src={image.previewUrl} alt={image.viewLabel ?? image.id} />
        )}
      </ThumbFrame>
      {action}
    </ThumbCard>
  );

  const tagButton = (label: string, onClick: () => void) => (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      style={{
        textTransform: "none",
        fontSize: rem(12),
        borderColor: EPOCUS_BORDER,
        color: EPOCUS_TEXT_PRIMARY,
      }}
    >
      {label}
    </Button>
  );

  return (
    <WorksheetSection label={`${regionLabel} Images`}>
      {tagged.length === 0 ? (
        <Hint>{`No images tagged to ${regionLabel} yet.`}</Hint>
      ) : (
        <ThumbGrid>
          {tagged.map((image) =>
            thumb(
              image,
              canTag &&
                tagButton("Untag", () => onTagImages?.([image.id], null)),
            ),
          )}
        </ThumbGrid>
      )}
      {untagged.length > 0 && canTag && (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ gap: 8, marginTop: 8 }}
          >
            <FindingLabel>{`Untagged (${untagged.length})`}</FindingLabel>
            {tagButton(`Tag all to ${regionLabel}`, () =>
              onTagImages?.(
                untagged.map((image) => image.id),
                region,
              ),
            )}
          </Box>
          <ThumbGrid>
            {untagged.map((image) =>
              thumb(
                image,
                tagButton(`Tag to ${regionLabel}`, () =>
                  onTagImages?.([image.id], region),
                ),
              ),
            )}
          </ThumbGrid>
        </>
      )}
    </WorksheetSection>
  );
};

const BillingLevelPrompt: FC<{
  data: MskWorksheetData;
  region: MskRegion;
  onChoose: (level: "complete" | "limited") => void;
  disabled: boolean;
}> = ({ data, region, onChoose, disabled }) => {
  if (!MSK_REGION_CONFIG[region].completeExamFindings) return null;
  const supported = supportsCompleteMskExam(data, region);
  const chosenComplete =
    mskRegionData(data, region).billingLevel === "complete";
  const level = mskBillingLevel(data, region);

  const action = (label: string, next: "complete" | "limited") =>
    !disabled && (
      <Button
        size="small"
        variant="outlined"
        onClick={() => onChoose(next)}
        style={{
          textTransform: "none",
          fontSize: rem(12),
          borderColor: EPOCUS_BORDER,
          color: EPOCUS_TEXT_PRIMARY,
          alignSelf: "flex-start",
        }}
      >
        {label}
      </Button>
    );

  if (level === "complete") {
    return (
      <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
        <Hint>Complete exam (76881) confirmed by you.</Hint>
        {action("Change to limited (76882)", "limited")}
      </Box>
    );
  }
  if (supported) {
    return (
      <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
        <Hint>
          You documented the joint space and every surrounding structure. This
          may support a complete exam (76881). Billing stays limited (76882)
          unless you confirm.
        </Hint>
        {action("Confirm complete (76881)", "complete")}
      </Box>
    );
  }
  return chosenComplete ? (
    <Hint>
      A complete exam needs the joint space and every surrounding structure
      assessed, so this region bills as limited (76882).
    </Hint>
  ) : null;
};

const RegionPanel: FC<{
  region: MskRegion;
  data: MskWorksheetData;
  onChange: (data: MskWorksheetData) => void;
  regionCode?: MskRegionCode;
  disabled: boolean;
  onOpenGuidedProcedure?: (region: MskRegion) => void;
}> = ({
  region,
  data,
  onChange,
  regionCode,
  disabled,
  onOpenGuidedProcedure,
}) => {
  const config = MSK_REGION_CONFIG[region];
  const regionData = mskRegionData(data, region);
  const isGuided =
    regionData.indication !== null &&
    "guided" in MSK_INDICATIONS[regionData.indication];

  const updateRegion = (patch: Partial<MskRegionData>) =>
    onChange({
      ...data,
      regions: { ...data.regions, [region]: { ...regionData, ...patch } },
    });

  const setFinding = (id: string, value: MskRegionData["findings"][string]) =>
    updateRegion({ findings: { ...regionData.findings, [id]: value } });

  const renderFindings = (placement: MskFindingPlacement | undefined) =>
    config.findings
      .filter(
        (finding) =>
          finding.placement === placement &&
          isMskFindingShown(data, region, finding),
      )
      .map((finding) => (
        <RegionFinding
          key={finding.id}
          finding={finding}
          value={regionData.findings[finding.id]}
          onChange={(value) => setFinding(finding.id, value)}
          disabled={disabled}
        />
      ));
  const headerFindings = renderFindings("header");

  return (
    <WorksheetColumns role="tabpanel" aria-label={`${config.label} findings`}>
      <WorksheetColumnLeft>
        {headerFindings.length > 0 && (
          <WorksheetSection>{headerFindings}</WorksheetSection>
        )}
        <WorksheetSection label="Side">
          <EnumChipSelect
            options={SIDE_OPTIONS}
            selected={regionData.side}
            onChange={(v) => updateRegion({ side: v as MskSide | null })}
            disabled={disabled}
          />
        </WorksheetSection>

        <WorksheetSection label="Indication">
          <EnumChipSelect
            options={mskRegionIndications(region).map((value) => ({
              value,
              label: indicationLabel("msk", value),
            }))}
            selected={regionData.indication}
            onChange={(v) =>
              updateRegion({ indication: v as MskIndication | null })
            }
            disabled={disabled}
          />
          {renderFindings("indication")}
          {isGuided && onOpenGuidedProcedure && !disabled && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onOpenGuidedProcedure(region)}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: rem(13),
                borderColor: EPOCUS_BORDER,
                color: EPOCUS_TEXT_PRIMARY,
                alignSelf: "flex-start",
              }}
            >
              Open guided joint injection worksheet
            </Button>
          )}
        </WorksheetSection>

        {config.views.length > 0 && (
          <WorksheetSection label="Views">
            <Hint>Tap to toggle obtained status</Hint>
            <ViewChecklist
              views={config.views.map((view) => ({
                key: mskViewKey(region, view.id),
                label: view.label,
              }))}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({ ...data, views: { ...data.views, [key]: checked } })
              }
              disabled={disabled}
            />
          </WorksheetSection>
        )}
      </WorksheetColumnLeft>

      <WorksheetColumnRight>
        <WorksheetSection>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            style={{ gap: 8 }}
          >
            <Typography
              style={{
                fontSize: rem(15),
                fontWeight: 700,
                color: EPOCUS_TEXT_PRIMARY,
              }}
            >
              {config.label} Findings
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              style={{ gap: 8 }}
            >
              {regionCode && (
                <BillingCptChip
                  code={regionCode.code}
                  modifier={regionCode.modifier}
                  label={`${config.label} code`}
                />
              )}
              {!disabled && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onChange(markMskRegionNormal(data, region))}
                  style={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: rem(13),
                    borderColor: EPOCUS_BORDER,
                    color: EPOCUS_TEXT_PRIMARY,
                  }}
                >
                  Mark {config.label} normal
                </Button>
              )}
            </Box>
          </Box>
          {regionCode && (
            <BillingLevelPrompt
              data={data}
              region={region}
              onChoose={(level) => updateRegion({ billingLevel: level })}
              disabled={disabled}
            />
          )}
          {renderFindings(undefined)}
          {config.technicallyLimited && (
            <ChipRow>
              <Chip
                label="Technically limited"
                variant="outlined"
                aria-pressed={regionData.technicallyLimited ?? false}
                style={chipStyle(regionData.technicallyLimited ?? false)}
                onClick={() =>
                  updateRegion({
                    technicallyLimited: !regionData.technicallyLimited,
                  })
                }
                disabled={disabled}
              />
            </ChipRow>
          )}
        </WorksheetSection>
        <WorksheetSection>{renderFindings("impression")}</WorksheetSection>
      </WorksheetColumnRight>
    </WorksheetColumns>
  );
};

export const MskWorksheet: FC<{
  data: MskWorksheetData;
  onChange: (data: MskWorksheetData) => void;
  readOnly: boolean;
  images?: readonly HydratedExamImage[];
  onTagImages?: (imageIds: string[], region: MskRegion | null) => void;
  regionCodes?: Partial<Record<MskRegion, MskRegionCode>>;
  onOpenGuidedProcedure?: (region: MskRegion) => void;
}> = ({
  data,
  onChange,
  readOnly,
  images = [],
  onTagImages,
  regionCodes,
  onOpenGuidedProcedure,
}) => {
  const scanned = scannedMskRegions(data);
  const visibleRegions =
    readOnly && scanned.length > 0 ? scanned : [...MSK_REGIONS];
  const [selectedRegion, setSelectedRegion] = useState<MskRegion>(
    scanned[0] ?? MSK_REGIONS[0],
  );
  const activeRegion = visibleRegions.includes(selectedRegion)
    ? selectedRegion
    : visibleRegions[0];
  const untaggedCount = readOnly ? 0 : untaggedMskImages(images).length;

  const handleChange = (next: MskWorksheetData) => onChange(normalizeMsk(next));

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <WorksheetSection label="Region">
        <Hint>
          Record each region you scanned on its own tab. Amber means started,
          green means complete.
        </Hint>
        <RegionTabs
          value={activeRegion}
          onChange={(_, region: MskRegion) => setSelectedRegion(region)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={TAB_INDICATOR_PROPS}
          aria-label="MSK regions"
        >
          {visibleRegions.map((region) => (
            <RegionTab
              key={region}
              value={region}
              label={<RegionTabLabel data={data} region={region} />}
              aria-label={MSK_REGION_CONFIG[region].label}
            />
          ))}
        </RegionTabs>
      </WorksheetSection>

      {untaggedCount > 0 && (
        <UntaggedBanner role="status">
          {untaggedCount === 1
            ? "1 image needs a region. Tag it to the region it shows before submitting."
            : `${untaggedCount} images need a region. Tag each one to the region it shows before submitting.`}
        </UntaggedBanner>
      )}

      <RegionPanel
        key={activeRegion}
        region={activeRegion}
        data={data}
        onChange={handleChange}
        regionCode={regionCodes?.[activeRegion]}
        disabled={readOnly}
        onOpenGuidedProcedure={onOpenGuidedProcedure}
      />

      {images.length > 0 && (
        <RegionImages
          region={activeRegion}
          images={images}
          onTagImages={onTagImages}
          readOnly={readOnly}
        />
      )}

      <WorksheetSection label="Notes">
        <NotesField
          value={data.notes}
          onChange={(v) => onChange({ ...data, notes: v })}
          disabled={readOnly}
        />
      </WorksheetSection>
    </Box>
  );
};
